import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Sidenavaction} from '../../../../../../../assets/js/application/sidenavaction';
import {BusinessEntityService} from '../../../../../../services/businessEntity.service';
import BusinessEntity from '../../../../../../services/businessEntity.model';
import {FarmSiteService} from '../../../../../../services/farmSite.service';
import FarmSiteModel from '../../../../../../services/farmSite.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component BusinessFarmfpFarmFarmsiteComponent
 * @selector app-business-farmfp-farm-farmsite
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `BusinessFarmfpFarmFarmsiteComponent` displays a list of Farm Sites for a specific Farm Business.
 * It is typically used in a Provider context where a provider views the farm sites of a client farm.
 *
 * ## Key Responsibilities:
 * 1. **Farm Site Listing**: Lists all farm sites associated with a specific farm business.
 * 2. **Navigation**: Provides navigation to view details of a farm site or to add/edit farm sites.
 *
 * ## User Roles:
 * - **Business Admin (Provider)**: View farm sites of client farms.
 * - **Admin**: System-wide access.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` retrieves route parameters (farmProviderid, farmId).
 * 2. **Data Fetching**: Fetches the business entity details and its farm sites.
 *
 * @service BusinessEntityService - For fetching business details.
 * @service FarmSiteService - For fetching farm sites.
 * @service CommonMethodsService - For error handling.
 */
@Component({
  selector: 'app-business-farmfp-farm-farmsite',
  templateUrl: './business-farmfp-farm-farmsite.component.html',
  styleUrls: ['./business-farmfp-farm-farmsite.component.scss']
})
export class BusinessFarmfpFarmFarmsiteComponent implements OnInit, OnDestroy {
  /** @type {BusinessEntity} The business entity model. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {string} Business ID from route parameters (Provider ID). */
  public paramsbusinessId: string;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {string} Farm ID from route parameters. */
  public farmId: string;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {any} Business parameter (isFarmsite). */
  public paramBusiness: any;

  /** @type {any} Application permissions. */
  public appPermission: any;

  /**
   * Constructor for BusinessFarmfpFarmFarmsiteComponent.
   *
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {Router} router - Angular Router.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {ActivatedRoute} route - Active route service.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public router: Router, public sidenavaction: Sidenavaction, public route: ActivatedRoute, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Retrieves route parameters.
   * - Fetches business entity and farm sites.
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    // get persontype
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.sidenavaction.x_navigation();
    this.route.params.subscribe(params => {
      this.paramsbusinessId = params.farmProviderid;
      this.farmId = params.farmId;
      this.paramBusiness = params.isFarmsite;
    });
    try {
      let promiseResults = new Array();
      await Promise.all([
        await this.businessEntityService.getOneByID(this.farmId),
        await await this.farmSiteService.getFarmeSitesByFarmId(this.farmId),
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.businessEntity = promiseResults[0];
      this.farmSites = promiseResults[1];
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for FarmSiteService and BusinessEntityService.
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
  }

  /**
   * @method goToBusinessEntityFarmSite
   * @description Navigates to the details page of a specific farm site.
   * @param {FarmSiteModel} farmSite - The selected farm site.
   */
  public goToBusinessEntityFarmSite(farmSite: FarmSiteModel) {
    this.router.navigate(['/home/BusinessEntityFarmSite', farmSite.id, this.businessEntity && this.businessEntity.id, this.paramsbusinessId, this.paramBusiness]);
  }

  /**
   * @method goToBusinessEntityFarms
   * @description Navigates to the add/edit farm site page.
   * @param {string} id - The farm site ID (for edit) or empty string (for add).
   * @param {string} type - The action type ('add' or 'edit').
   */
  public goToBusinessEntityFarms(id: string, type: string) {
    if (type == 'add') {
      this.router.navigate(['/home/BusinessEntityFarms', this.businessEntity?.id, this.paramBusiness],
        {
          queryParams: {
            site: '',
            actionType: type
          }
        }
      );
    } else if (type == 'edit') {
      this.router.navigate(['/home/BusinessEntityFarms', this.businessEntity?.id, this.paramBusiness],
        {
          queryParams: {
            site: id,
            actionType: type
          }
        }
      );
    }
  }
}
